import detectEthereumProvider from '@metamask/detect-provider';
import Web3 from 'web3';
import Network from './network';
import tokenAbi from './abi/token.json';
import Config from '../../config';
import BigNumberUtil from './util/BigNumberUtil';
import Claim from './claim';

class Blockv {
  async init() {
    this.network = new Network(Config.server);
    await detectEthereumProvider();
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', this.handleAccountsChanged);
      window.ethereum.on('networkChanged', this.handleNetworkChanged);
      // eslint-disable-next-line new-cap
      this.web3 = new Web3(window.ethereum);
      this.claim = new Claim(this);
      this.tokenInstance = new this.web3.eth.Contract(tokenAbi.abi, Config.tokenContractAddress);
    }
  }

  set onDisconnect(value) {
    this.disconnectListener = value;
  }

  get onDisconnect() {
    return this.disconnectListener;
  }

  set onNetworkChange(value) {
    this.networkListener = value;
  }

  get onNetworkChange() {
    return this.networkListener;
  }

  set onAccountChange(value) {
    this.accountListener = value;
  }

  get onAccountChange() {
    return this.accountListener;
  }

  selectAccount() {
    return window.ethereum.request({
      method: 'wallet_requestPermissions',
      params: [
        {
          eth_accounts: {},
        },
      ],
    });
  }

  getAccount() {
    if (this.account) {
      return Promise.resolve(this.account);
    }
    return new Promise((resolve) => {
      this.web3.eth.getAccounts((error, accounts) => {
        if (accounts?.length > 0) {
          const [account] = accounts;
          this.account = account;
          resolve(account);
        } else {
          resolve(null);
        }
      });
    });
  }

  checkConnected() {
    return new Promise((resolve, reject) => {
      if (!this.web3) reject(new Error('not supported'));
      this.web3.eth.getAccounts((error, accounts) => {
        if (error != null) reject(error);
        else if (accounts.length === 0) resolve(false);
        else resolve(true);
      });
    });
  }

  getBalance() {
    return this.getNetworkId().then((networkId) => {
      if (networkId === Config.eth.chainId) {
        return this.getAccount()
          .then((account) => {
            return this.tokenInstance.methods.balanceOf(account).call();
          })
          .then((result) => {
            const value = BigNumberUtil.toNumber(result, Config.tokenDecimals, 2);
            return { value, networkId };
          });
      }
      if (networkId === Config.blockv.chainId) {
        return this.getAccount().then((account) => {
          return new Promise((resolve, reject) => {
            this.web3.eth.getBalance(account, (error, balance) => {
              if (error) {
                reject(error);
                return;
              }
              const value = BigNumberUtil.toNumber(balance, Config.tokenDecimals, 2);
              resolve({ value, networkId });
            });
          });
        });
      }
      return { value: '', networkId };
    });
  }

  watchBalance(callback) {
    const balance = () => {
      this.getBalance()
        .then((result) => {
          callback?.(result);
        })
        .catch((error) => {
          console.error(error);
          callback?.(null, error);
        });
    };
    balance();
    const id = setInterval(balance, 5 * 1000);

    return () => {
      clearInterval(id);
    };
  }

  connect() {
    return window.ethereum
      .request({ method: 'eth_requestAccounts' })
      .then(this.handleAccountsChanged)
      .catch((error) => {
        console.error(error);
        return [];
      });
  }

  handleAccountsChanged = async (accounts) => {
    if ((accounts?.length ?? 0) > 0) {
      this.web3.eth.getAccounts((error, ethAccounts) => {
        if (ethAccounts?.length > 0) {
          const [account] = ethAccounts;
          this.account = account;
          this.accountListener?.(account);
        }
      });
    } else {
      this.account = null;
      this.disconnectListener?.();
    }
  };

  handleNetworkChanged = async (networkId) => {
    this.currentNetwork = `${networkId}`;
    this.networkListener?.(`${networkId}`);
  };

  viewTransaction(transaction, networkId) {
    if (networkId === Config.eth.chainId) {
      window.open(Config.eth.blockchainViewer.replace('{{transaction}}', transaction));
    } else if (networkId === Config.blockv.chainId) {
      window.open(Config.blockv.blockchainViewer.replace('{{transaction}}', transaction));
    }
  }

  switchNetwork(chain) {
    const chainId = `0x${Number.parseInt(chain, 10).toString(16)}`;
    return window.ethereum
      .request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId }],
      })
      .catch((error) => {
        if (chain === Config.blockv.chainId && error.code === 4902) {
          return window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId,
                rpcUrls: [Config.blockv.rpcUrl],
                chainName: Config.blockv.chainName,
                nativeCurrency: {
                  name: 'VEE',
                  symbol: 'VEE',
                  decimals: 18,
                },
                blockExplorerUrls: [
                  Config.blockv.blockchainViewer.replace('/tx/{{transaction}}', ''),
                ],
              },
            ],
          });
        }
        throw error;
      });
  }

  getNetworkId() {
    return this.web3.eth.getChainId().then((chainId) => {
      return `${chainId}`;
    });
  }

  deposit(amount, onTransactionHash) {
    return new Promise((resolved, reject) => {
      this.tokenInstance.methods
        .transfer(Config.depositAddress, amount.toString(10))
        .send({
          from: this.account,
        })
        .on('transactionHash', (hash) => {
          onTransactionHash?.(hash);
        })
        .on('receipt', (receipt) => {
          resolved(receipt);
        })
        .on('error', (error) => {
          reject(error);
        });
    });
  }

  withdraw(amount, onTransactionHash) {
    return new Promise((resolved, reject) => {
      this.web3.eth
        .sendTransaction({
          from: this.account,
          to: Config.withdrawAddress,
          value: amount.toString(10),
          gasPrice: Web3.utils.toWei('10000', 'gwei'),
        })
        .on('transactionHash', (hash) => {
          onTransactionHash?.(hash);
        })
        .on('receipt', (receipt) => {
          resolved(receipt);
        })
        .on('error', (error) => {
          reject(error);
        });
    });
  }
}
export default new Blockv();
